import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { MonthlyExpenseBranchRow, MonthlyExpenseGroup } from "../../types/monthly-expense.type";
import { formatNumber } from "../../utils";
import GridCellInput from "../common/EditableGrid/GridCellInput";
import { BODY_ROW_SX } from "../DailyExpense/tableStyles";
import EstimateCell from "./EstimateCell";
import ExtrasCell from "./ExtrasCell";
import {
  ADJUSTED_SX,
  CELL_SX,
  GROUP_SEPARATOR_SX,
  TOTAL_COLUMN_SX,
  pinnedSx,
} from "./closeTableStyles";
import { EMPTY_TEXT } from "./constants";
import {
  BranchDraft,
  BranchFigures,
  CellError,
  CloseCellTarget,
  ESTIMATE_SIDES,
  EstimateSide,
  dailyCellKeyOf,
  extrasTotalOf,
  targetKeyOf,
} from "./monthlyExpenseDraft";
import { CloseColumn, columnKeyOf, isGroupStart } from "./monthlyExpenseLayout";
import { AmountView, viewAmountOf } from "./monthlyExpenseMath";

const ESTIMATE_LABEL: Record<EstimateSide, string> = { SALES: "Sales", MANAGER: "Manager" };

export interface CloseRowHandlers {
  onCommit: (target: CloseCellTarget, text: string) => void;
  onRevertEstimate: (branchCode: string, side: EstimateSide) => void;
  onAddExtra: (branchCode: string, group: MonthlyExpenseGroup) => void;
  onRemoveExtra: (branchCode: string, key: string) => void;
}

interface CloseTableRowProps extends CloseRowHandlers {
  row: MonthlyExpenseBranchRow;
  draft: BranchDraft;
  figures: BranchFigures;
  errors: Record<string, CellError>;
  columns: CloseColumn[];
  rowIndex: number;
  view: AmountView;
  isEditable: boolean;
  estimateTitle: string;
  monthTitle: string;
}

/**
 * One branch: the pinned divisor, each visible column (an input in ยอดเดือน on an open month, the
 * divided figure otherwise), and the two estimate cells. Grid rows are branches, grid columns the
 * visible column order, so Tab / Enter walk the table like a sheet.
 */
export default function CloseTableRow({
  row,
  draft,
  figures,
  errors,
  columns,
  rowIndex,
  view,
  isEditable,
  estimateTitle,
  monthTitle,
  onCommit,
  onRevertEstimate,
  onAddExtra,
  onRemoveExtra,
}: Readonly<CloseTableRowProps>) {
  const branchCode = row.branch_code;
  const isAmountEditable = isEditable && view === "MONTH";
  const viewText = (amount: number) => {
    const value = viewAmountOf(amount, view, row.sale_days, row.route_count);
    return value === null ? EMPTY_TEXT : formatNumber(value);
  };

  const amountCell = (column: CloseColumn, columnIndex: number) => {
    const separator = isGroupStart(columns, columnIndex) ? GROUP_SEPARATOR_SX : {};
    if (column.kind === "GROUP_TOTAL") {
      return (
        <TableCell key={columnKeyOf(column)} sx={[CELL_SX, TOTAL_COLUMN_SX, separator]}>
          {viewText(figures.groupTotals[column.groupKey])}
        </TableCell>
      );
    }
    if (column.kind === "EXTRA") {
      return (
        <ExtrasCell
          key={columnKeyOf(column)}
          branchCode={branchCode}
          branchName={row.branch_name}
          group={column.groupKey}
          extras={draft.extras.filter((extra) => extra.group === column.groupKey)}
          viewAmount={viewAmountOf(extrasTotalOf(draft, column.groupKey), view, row.sale_days, row.route_count)}
          isEditable={isEditable}
          monthTitle={monthTitle}
          onAdd={onAddExtra}
          onRemove={onRemoveExtra}
        />
      );
    }
    const target: CloseCellTarget =
      column.kind === "DAILY"
        ? { kind: "DAILY", branchCode, categoryCode: column.categoryCode, group: column.group }
        : { kind: "ITEM", branchCode, categoryCode: column.categoryCode };
    const amount =
      column.kind === "DAILY"
        ? (draft.daily[dailyCellKeyOf(column.categoryCode, column.group)] ?? 0)
        : (draft.items[column.categoryCode] ?? 0);
    const systemAmount =
      column.kind === "DAILY"
        ? row.daily.find(
            (cell) => cell.category_code === column.categoryCode && cell.expense_group === column.group,
          )?.system_amount
        : undefined;
    const isOverridden = systemAmount !== undefined && amount !== systemAmount;
    const overrideText = isOverridden ? `แก้ทับแล้ว · ค่าจากรายวัน ${formatNumber(systemAmount)}` : "";
    const error = errors[targetKeyOf(target)];
    return (
      <TableCell key={columnKeyOf(column)} sx={[CELL_SX, separator]}>
        {isAmountEditable ? (
          <GridCellInput
            position={{ row: rowIndex, column: columnIndex }}
            target={target}
            text={error ? error.text : formatNumber(amount)}
            onCommit={onCommit}
            ariaLabel={`${branchCode} ${column.label}`}
            hasError={Boolean(error)}
            tooltip={error?.message || overrideText || column.label}
            inputSx={isOverridden ? ADJUSTED_SX : {}}
          />
        ) : (
          <Tooltip title={overrideText}>
            <Typography component="span" variant="body2" sx={[{ px: 0.75 }, isOverridden ? ADJUSTED_SX : {}]}>
              {viewText(amount)}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
    );
  };

  return (
    <TableRow sx={BODY_ROW_SX}>
      <TableCell sx={[CELL_SX, pinnedSx("BRANCH"), { textAlign: "left" }]}>
        <Typography component="span" variant="body2" fontWeight={500}>
          {branchCode}{" "}
        </Typography>
        <Typography component="span" variant="body2" color="text.secondary">
          {row.branch_name}
        </Typography>
      </TableCell>
      <TableCell align="center" sx={[CELL_SX, pinnedSx("DAYS")]}>
        {row.sale_days}
      </TableCell>
      <TableCell align="center" sx={[CELL_SX, pinnedSx("ROUTES")]}>
        {row.route_count}
      </TableCell>
      {columns.map(amountCell)}
      {ESTIMATE_SIDES.map((side, sideIndex) => (
        <EstimateCell
          key={side}
          branchCode={branchCode}
          side={side}
          figures={figures.estimate[side]}
          error={errors[targetKeyOf({ kind: "ESTIMATE", branchCode, side })]}
          position={{ row: rowIndex, column: columns.length + sideIndex }}
          isEditable={isEditable}
          label={`${branchCode} ${ESTIMATE_LABEL[side]}`}
          estimateTitle={estimateTitle}
          onCommit={onCommit}
          onRevert={onRevertEstimate}
        />
      ))}
    </TableRow>
  );
}
